// CarrierConfigPage
// This page renders the Carriers configuration section as a standalone route,
// keeping per-tab isolation and persistence at the page level.
// It replaces the "config" section of CarrierPage so we keep a 1:1 relation
// between route and page.
//
// Route that should land here (configured in your router):
//   /carriers  -> renders CarrierConfigPage
//
// Notes:
// - We keep the same look & feel as the previous CarrierPage "config" section:
//   title area + responsive layout + main content.
// - Per-tab state used for CRUD (operation, CarrierLine, etc.) is preserved
//   via M_State, exactly as before in CarrierConfig.
// - We update the tab title based on the i18n key that matches the
//   MenuDisplay value from the backend seed ("MenuCarriersManegementLabel").
//
// If you want to remove the old CarrierPage later, point the /carriers route
// directly to this CarrierConfigPage.

import PropTypes from 'prop-types';
import React, {
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import {
  LoadingContext,
  NavigateContext,
  M_StateActions,
  MTable,
} from 'src/M_Components';

export default function CarrierConfigPage({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(false);
  const matches = useMediaQuery('(min-width:900px)');

  // ------------------------------------------------------------------------------------------------------------
  // Read current selected tab id from global state so we can safely rename the tab caption, if desired.
  // ------------------------------------------------------------------------------------------------------------
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  // ------------------------------------------------------------------------------------------------------------
  // EFFECT
  // Update tab title when language changes. This keeps the tab label aligned with the menu display:
  //   MenuCarriersManegementLabel (submenu under the Carriers root).
  //
  // IMPORTANT:
  // - This is optional but useful when the user changes language at runtime.
  // - It assumes the tab was initially created using the same menu display key.
  // ------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTab) return;
    renameTab(String(selectedTab), t('MenuCarriersManegementLabel'));
  }, [selectedTab, renameTab, t]);

  // memoized array to avoid re-renders
  const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

  // stable route for CRUD
  const CRUD_ROUTE = '/home/carriers/carrierConfigCrud';

  //===========================================================================================
  // opens/renames the tab and saves necessary state - stable between renders
  // ===========================================================================================
  const openCarrierCrud = useCallback(
    (operation, data) => {
      // data is the row data for Edit/View, undefined for Create
      dispatch(M_StateActions.addM_State({ key: 'operation', value: operation })); // Save operation in state

      // For Edit/View or copy ensure a row is selected
      if (['Edit', 'Copy', 'View'].includes(operation)) {
        if (!data) {
          // No row selected, show error and return
          toastMessages('error', '', t('SelectedLine'));
          return;
        }
        // Save selected carrier data in state
        dispatch(M_StateActions.addM_State({ key: 'CarrierLine', value: data }));
      }

      // Create or rename the tab
      const tabLabel =
        operation === 'Create'
          ? t('LabelNewCarrier')
          : operation === 'View'
          ? `${t('LabelViewCarrier')} "${data?.Name ?? ''}"`
          : `${t('LabelEditCarrier')} "${data?.Name ?? ''}"`;

      onCreateTab(CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  //===========================================================================================
  // memoized replace functions for table actions
  //===========================================================================================
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openCarrierCrud('View', row)],
        ['Create', () => openCarrierCrud('Create')],
        ['Edit', (row) => openCarrierCrud('Edit', row)],
      ]),
    [openCarrierCrud]
  );

  return (
    <div className="divGlobalSettings" style={{ padding: '20px' }}>
      {/* Title area shows the section title based on i18n keys */}
      <div
        className="titleSettings"
        style={{
          color: ctx.secondaryColor,
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        {t('MenuCarriersManegementLabel')}
      </div>

      {/* Keep layout responsive as before. The main content is the Carriers list + CRUD actions. */}
      <div style={matches ? { display: 'flex' } : {}}>
        <div className="settingsPageMainDiv" style={{ width: '100%' }}>
          <div className="divGlobalSettings">
            <div className="subTitleSettings">
              <div>
                <div style={{ color: ctx.secondaryColor }}>{t('Carriers')}</div>
                <div className="labelSubtitleSettings">{t('InfoCarriers')}</div>
              </div>
            </div>

            <MTable
              filtersOnTable // Show filters on table header
              entityName="Carriers"
              roleName="Carriers"
              rolePermissions={roles?.map((vl) => vl.Value)} // Extract permission values for this module
              removeContextItems={removeContextItems} // Remove specified context menu items
              refresh={refresh} // Refresh state to trigger data reload
              setRefresh={setRefresh} // Function to update refresh state
              useSessionStorage={false} // Disable session storage for this table
              replaceFunctions={replaceFunctions} // Replace default actions with custom functions
              tabsRailWidth={250}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

CarrierConfigPage.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};
